import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "Types";
import { remove, some } from "lodash";

export interface Action {
  action: string;
  showProgress?: boolean;
  showOverlay?: boolean;
}

export interface ProgressState {
  actions: Action[];
}

export const initialState: ProgressState = {
  actions: [],
};

export const progressSlice = createSlice({
  name: "progress",
  initialState,
  reducers: {
    addProgressAction: (state: RootState, action: PayloadAction<Action>) => {
      const defaultActionTemplate = { showProgress: false, showOverlay: false };
      state.actions.push({ ...defaultActionTemplate, ...action.payload });
    },
    removeProgressAction: (state: RootState, action: PayloadAction<string>) => {
      remove(state.actions, (c: Action) => c?.action === action.payload);
    },
    removeMatchingProgressAction: (
      state: RootState,
      action: PayloadAction<string>,
    ) => {
      remove(state.actions, (c: Action) =>
        c?.action?.startsWith(action.payload),
      );
    },
    clearProgressActions: (state: RootState) => {
      state.actions = [];
    },
  },
});

export const {
  addProgressAction,
  removeProgressAction,
  removeMatchingProgressAction,
  clearProgressActions,
} = progressSlice.actions;

export const selectProgress = (state: RootState) => state.progress.actions;

export const selectProgressActions = createSelector(
  selectProgress,
  (progressActions) => progressActions?.map((action: Action) => action.action),
);

export const showProgress = createSelector(selectProgress, (progressState) =>
  some(progressState, { showProgress: true }),
);

export const showOverlay = createSelector(selectProgress, (progressState) =>
  some(progressState, { showOverlay: true }),
);

export const isActionInProgressSelector = createSelector(
  [selectProgressActions, (_state: RootState, action) => action],
  (progressActions, action) => progressActions?.includes(action) ?? false,
);

export const selectActionInProgress = (action: string) =>
  createSelector(
    (state: RootState) => state,
    (state) => isActionInProgressSelector(state, action),
  );

export default progressSlice.reducer;
